/**
 * Purchase orders and receiving API calls.
 */

import client, { fetchAllPages } from './client';

export async function fetchPurchaseOrders(params = {}) {
  // All pages — OrdersTab enumerates every PO on a job (each PO
  // renders as its own card with nested lines) and CostingTab sums
  // cost across ALL PO lines for the P&L rollup. Truncation would
  // silently drop POs from both surfaces — same class of bug as the
  // fetchVendorQuotes report.
  return fetchAllPages('/purchasing/', params);
}

export async function fetchPurchaseOrder(poId) {
  const { data } = await client.get(`/purchasing/${poId}/`);
  return data.data;
}

export async function confirmSelections(jobId) {
  const { data } = await client.post('/purchasing/confirm-selections/', { job_id: jobId });
  return data.data;
}

export async function sendPo(poId, options = {}) {
  const { data } = await client.post(`/purchasing/${poId}/send/`, options);
  return data.data;
}

export async function requestStatus(poId) {
  const { data } = await client.post(`/purchasing/${poId}/request-status/`);
  return data.data;
}

export async function receiveLine(lineId, payload) {
  const { data } = await client.post(`/purchasing/lines/${lineId}/receive/`, payload);
  return data.data;
}

export async function fetchPoLines(params = {}) {
  const { data } = await client.get('/purchasing/lines/', { params });
  return data.data || data.results || [];
}

export async function fetchReceivingLog(params = {}) {
  const { data } = await client.get('/purchasing/receiving-log/', { params });
  return data.data || data.results || [];
}

export async function receiveStock(bomLineId, payload) {
  const { data } = await client.post(`/jobs/bom-lines/${bomLineId}/receive-stock/`, payload);
  return data.data;
}

export async function fetchPoLinesForBomLine(bomLineId) {
  const { data } = await client.get('/purchasing/lines/', { params: { job_bom_line: bomLineId, page_size: 20 } });
  return data.data || data.results || [];
}

export async function receiveAllPoLines(poId, lines, notes = '') {
  const { data } = await client.post(`/purchasing/${poId}/receive-all/`, { lines, notes });
  return data.data;
}

export async function previewPo(jobId, file, aiInstructions = '') {
  const formData = new FormData();
  formData.append('job_id', jobId);
  formData.append('file', file);
  if (aiInstructions) formData.append('ai_instructions', aiInstructions);
  const { data } = await client.post('/purchasing/preview-po/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000, // 60s — AI extraction takes time
  });
  return data.data;
}

export async function confirmPo(jobId, file, aiData, { addUnmatchedToBom = false, newVendorEmail = '', oneOffPoIndices = [] } = {}) {
  const formData = new FormData();
  formData.append('job_id', jobId);
  formData.append('file', file);
  formData.append('ai_data', JSON.stringify(aiData));
  formData.append('add_unmatched_to_bom', addUnmatchedToBom ? 'true' : 'false');
  if (newVendorEmail) formData.append('new_vendor_email', newVendorEmail);
  if (oneOffPoIndices && oneOffPoIndices.length > 0) {
    formData.append('one_off_po_indices', JSON.stringify(oneOffPoIndices));
  }
  const { data } = await client.post('/purchasing/confirm-po/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return data.data;
}

/** @deprecated Use previewPo + confirmPo instead */
export async function uploadManualPo(jobId, file) {
  const formData = new FormData();
  formData.append('job_id', jobId);
  formData.append('file', file);
  const { data } = await client.post('/purchasing/upload-po/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return data.data;
}

export async function updatePoLine(lineId, payload) {
  const { data } = await client.patch(`/purchasing/lines/${lineId}/`, payload);
  return data.data || data;
}

export async function updatePurchaseOrder(poId, payload) {
  const { data } = await client.patch(`/purchasing/${poId}/`, payload);
  return data.data || data;
}

export async function addUnmatchedToBom(poId) {
  const { data } = await client.post(`/purchasing/${poId}/add-unmatched-to-bom/`);
  return data.data;
}

/**
 * Search open POs by PO number, vendor name, or job number.
 * Returns matches with job info for quick navigation.
 */
export async function searchPurchaseOrders(query) {
  if (!query || query.length < 2) return [];
  const { data } = await client.get('/purchasing/search/', { params: { q: query } });
  return data.data || [];
}

// ---------------------------------------------------------------------------
// BULK PURCHASING — Purchasing Tab API
// ---------------------------------------------------------------------------

/**
 * Get all pending parts (need_to_order > 0) grouped by vendor → part → jobs.
 * Used by the Purchasing tab on the dashboard.
 */
export async function fetchPendingParts() {
  const { data } = await client.get('/purchasing/pending-parts/');
  return data.data;
}

/**
 * Preview a bulk PO upload.
 * AI extracts and matches across ALL active jobs.
 * READ-ONLY — no database changes.
 */
export async function previewBulkPo(file, aiInstructions = '') {
  const formData = new FormData();
  formData.append('file', file);
  if (aiInstructions) formData.append('ai_instructions', aiInstructions);
  const { data } = await client.post('/purchasing/preview-bulk-po/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000, // 60s — AI extraction takes time
  });
  return data.data;
}

/**
 * Confirm and process a bulk PO.
 * Creates PO lines across multiple jobs based on allocations.
 * Excess qty is treated as stock (not tracked).
 * @param {File} file - The PDF file
 * @param {object} aiData - AI extraction result from preview
 * @param {array} allocations - Array of {bom_line_id, job_id, job_number, qty_allocated, ...}
 * @param {object} [options]
 * @param {string} [options.newVendorEmail] - Email to save on a new/existing vendor (when preview flagged vendor_email_missing)
 * @param {string} [options.aiInstructions] - Track B Phase 1: original user instructions, round-tripped from preview for audit persistence.
 * @param {object|null} [options.aiAllocationPlan] - Track B Phase 1: AI-generated allocation plan from preview, persisted on the PO for audit. Pass null when no instructions.
 * @param {array|null} [options.approvedActions] - Track B Phase 1: list of approved propose_bom_add indices into aiAllocationPlan.allocations[]. Phase 1 has no approval UI; pass null.
 */
export async function confirmBulkPo(file, aiData, allocations, {
  newVendorEmail = '',
  aiInstructions = '',
  aiAllocationPlan = null,
  approvedActions = null,
} = {}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ai_data', JSON.stringify(aiData));
  formData.append('allocations', JSON.stringify(allocations));
  if (newVendorEmail) formData.append('new_vendor_email', newVendorEmail);
  // Track B Phase 1 audit-only fields. Always send, even when empty, so
  // the backend persists a consistent shape on PurchaseOrder. Empty
  // string / null / [] are valid persisted values per the model schema.
  formData.append('ai_instructions', aiInstructions || '');
  if (aiAllocationPlan !== null && aiAllocationPlan !== undefined) {
    formData.append('ai_allocation_plan', JSON.stringify(aiAllocationPlan));
  }
  if (approvedActions !== null && approvedActions !== undefined) {
    formData.append('approved_actions', JSON.stringify(approvedActions));
  }
  const { data } = await client.post('/purchasing/confirm-bulk-po/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return data.data;
}

// ---------------------------------------------------------------------------
// Phase 4A — Packing slip review (admin-only)
// ---------------------------------------------------------------------------

/**
 * Fetch a paginated list of packing slip pages.
 * @param {object} params - { queue: 'needs_review' | 'recent_success', page: 1 }
 */
export async function fetchPackingSlipPages(params = {}) {
  const { data } = await client.get('/purchasing/packing-slip-pages/', { params });
  return {
    items: data.data || data.results || [],
    count: data.count ?? (data.data || data.results || []).length,
    next: data.next || null,
    previous: data.previous || null,
  };
}

/**
 * Fetch a single packing slip page detail.
 */
export async function fetchPackingSlipPage(id) {
  const { data } = await client.get(`/purchasing/packing-slip-pages/${id}/`);
  return data.data;
}

/**
 * Download the rendered page image as a Blob URL for use in <img src>.
 * Caller is responsible for URL.revokeObjectURL() when the URL is no longer needed.
 */
export async function fetchPackingSlipPageImageBlobUrl(id) {
  const response = await client.get(`/purchasing/packing-slip-pages/${id}/image/`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
}

/**
 * Mark a page as triaged (drops out of the needs-review queue).
 * Underlying status is preserved.
 */
export async function dismissPackingSlipPage(id) {
  const { data } = await client.post(`/purchasing/packing-slip-pages/${id}/dismiss/`);
  return data.data;
}

/**
 * Reset the page to PENDING and queue an async reprocess.
 * Returns the updated page (status will be PENDING in the response;
 * the worker will move it to PROCESSING then a terminal state).
 */
export async function reprocessPackingSlipPage(id) {
  const { data } = await client.post(`/purchasing/packing-slip-pages/${id}/reprocess/`);
  return data.data;
}

/**
 * Lightweight count for the nav badge.
 * Returns { count: number }.
 */
export async function fetchPackingSlipNeedsReviewCount() {
  const { data } = await client.get('/purchasing/packing-slip-pages/needs-review-count/');
  return data.count ?? 0;
}
